import { useCallback, useEffect, useState } from 'react';
import { Button, Label, ListBox, ListBoxItem, Select } from '@heroui/react';

import CommentBubble from '../../common/CommentBubble';
import MentionText from '../../common/MentionText';
import MentionTextArea from '../../common/MentionTextArea';
import Notice from '../../common/Notice';
import TimeAgo from '../../common/TimeAgo';
import { SkeletonText } from '../../common/Skeleton';
import RelevanceChip from '../../common/RelevanceChip';
import judgementsService from '../../../services/judgements.service';
import { JUDGEMENTS, MENTIONS, mentionMessageOf, messageOf, SELECT_PLACEHOLDER } from '../../../i18n/el';
import { getUser } from '../../../services/session';
import { useMentionMembers } from '../../../services/collectionMembers';

// Σχόλια (ΛΑ-16) και αξιολογήσεις (ΛΑ-17) ενός πόρου, **μέσα σε μία συλλογή**.
//
// Το `collectionId` δεν είναι προαιρετικό. Με την **D9** ο πόρος είναι κοινός: ένα
// έγγραφο σε πολλές συλλογές. Η κρίση όμως ανήκει στη σχέση πόρου και συλλογής, και
// σχόλιο χωρίς συλλογή δεν έχει πού να γραφτεί ούτε ποια σχόλια να δείξει.

const RELEVANCE_OPTIONS = [
    { id: 'high', label: JUDGEMENTS.relevance.high },
    { id: 'medium', label: JUDGEMENTS.relevance.medium },
    { id: 'low', label: JUDGEMENTS.relevance.low }
];

export default function ResourceJudgements({
    resourceId,
    collectionId,
    canWrite = false,
    // Ποια από τις δύο όψεις φαίνεται. Ελεγχόμενο από το παράθυρο του πόρου, που
    // κρατά **μία** κατάσταση για τις τρεις καρτέλες. Η προεπιλογή αφορά μόνο τη
    // χρήση εκτός παραθύρου, π.χ. στη βιβλιοθήκη συστατικών.
    pane = 'comments'
}) {
    const [comments, setComments] = useState([]);
    const [ratings, setRatings] = useState([]);
    const [isLoading, setLoading] = useState(true);
    // **Τρία σφάλματα και όχι ένα, γιατί εμφανίζονται σε τρία διαφορετικά σημεία.**
    //
    // Το `error` ήταν ένα και αποδιδόταν στην **κορυφή** του πίνακα κρίσεων. Ο
    // πίνακας όμως είναι στήλη με δική της κύλιση, και οι δύο φόρμες βρίσκονται στο
    // **κάτω** άκρο της, μετά τη λίστα: όταν η υποβολή αποτύγχανε, το μήνυμα
    // γραφόταν σωστά, σε θέση εκτός οθόνης. Ο χρήστης έβλεπε τη φόρμα να μη
    // μεταβάλλεται και συμπέραινε ότι η κρίση καταγράφηκε.
    //
    // Η ασυμμετρία ήταν και ορατή στον ίδιο τον κώδικα: το μήνυμα **επιτυχίας**
    // αποδιδόταν μέσα στη φόρμα, δίπλα στο κουμπί, ενώ το μήνυμα **αποτυχίας** δύο
    // οθόνες πιο πάνω. Το σφάλμα ανήκει εκεί όπου έγινε η ενέργεια (ΜΛΑ-03).
    const [error, setError] = useState('');
    const [commentError, setCommentError] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [notice, setNotice] = useState('');

    const [text, setText] = useState('');
    const [isSendingComment, setSendingComment] = useState(false);

    const [relevance, setRelevance] = useState('high');
    const [justification, setJustification] = useState('');
    const [isSavingRating, setSavingRating] = useState(false);

    const [isEditingRating, setEditingRating] = useState(false);

    // D44: τα μέλη ζητιούνται **μόνο** όταν ο χρήστης πληκτρολογήσει «@», μία φορά
    // ανά συλλογή. Η στήλη των κρίσεων δεν τα έχει από τον γονέα της — ο μόνος
    // γονέας της είναι η κάρτα του πόρου, που δεν τα κρατά.
    const [wantsMembers, setWantsMembers] = useState(false);
    const { members, isLoadingMembers } = useMentionMembers(collectionId, {
        isActive: wantsMembers
    });
    const onMentionStart = useCallback(() => setWantsMembers(true), []);

    const currentUserId = getUser()?.id ?? null;
    const myRating = ratings.find((rating) => rating.user?.id === currentUserId) ?? null;
    // Πόσοι **άλλοι** έκριναν, μέσα σε **αυτή** τη συλλογή.
    const othersCount = ratings.filter((rating) => rating.user?.id !== currentUserId).length;

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');

        Promise.all([
            judgementsService.getComments(resourceId, collectionId),
            judgementsService.getRatings(resourceId, collectionId)
        ]).then(
            ([commentPage, ratingPage]) => {
                if (!active) return;
                setComments(commentPage.data);
                setRatings(ratingPage.data);
                setLoading(false);
            },
            (apiError) => {
                if (!active) return;
                setError(messageOf(apiError));
                setLoading(false);
            }
        );

        return () => {
            active = false;
        };
    }, [resourceId, collectionId]);

    // Η φόρμα δείχνει την **υπάρχουσα** εκτίμηση του χρήστη: η D14 ορίζει μία ανά
    // τριάδα, οπότε η δεύτερη υποβολή είναι μεταβολή γνώμης. Φόρμα που ξεκινά κενή
    // θα έκρυβε ότι υπάρχει ήδη καταγεγραμμένη κρίση.
    useEffect(() => {
        if (!myRating) return;
        setRelevance(myRating.relevance);
        setJustification(myRating.justification ?? '');
    }, [myRating?.id, myRating?.relevance, myRating?.justification]);

    function submitComment(event) {
        event.preventDefault();
        if (!text.trim()) return;

        setSendingComment(true);
        setCommentError('');

        judgementsService.addComment(resourceId, collectionId, text.trim()).then(
            (created) => {
                setComments((current) => [created, ...current]);
                setText('');
                setSendingComment(false);
            },
            (apiError) => {
                setCommentError(mentionMessageOf(apiError));
                setSendingComment(false);
            }
        );
    }

    function submitRating(event) {
        event.preventDefault();
        setSavingRating(true);
        setRatingError('');
        setNotice('');

        judgementsService.saveRating(resourceId, collectionId, { relevance, justification }).then(
            (saved) => {
                setRatings((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
                setNotice(myRating ? JUDGEMENTS.ratingUpdated : JUDGEMENTS.ratingSaved);
                setSavingRating(false);
                // Κλείδωμα αμέσως μετά την επιτυχία: η καταγεγραμμένη εκτίμηση είναι
                // πλέον γεγονός, και η φόρμα δεν έχει λόγο να μένει ανοιχτή.
                setEditingRating(false);
            },
            (apiError) => {
                setRatingError(mentionMessageOf(apiError));
                setSavingRating(false);
            }
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col gap-2">
                <SkeletonText width="40%" />
                <SkeletonText />
                <SkeletonText width="70%" />
            </div>
        );
    }

    return (
        <section className="flex flex-col gap-6">
            {/* Μόνο η **φόρτωση** αναφέρεται εδώ: αφορά ολόκληρο τον πίνακα και δεν
                υπάρχει άλλο σημείο να μπει. Τα σφάλματα υποβολής μπαίνουν μέσα στη
                φόρμα που απέτυχε. */}
            {error && <Notice status="danger" title={error} />}

            <p className="text-xs text-muted">{JUDGEMENTS.scopeNote}</p>

            <div className={`flex-col gap-3 ${pane === 'comments' ? 'flex' : 'hidden'}`}>
                <h4 className="text-sm font-semibold">{JUDGEMENTS.comments}</h4>

                {comments.length === 0 && <p className="text-sm text-muted">{JUDGEMENTS.noComments}</p>}

                {/* Β10β: ταυτότητα συντάκτη και κείμενο σε φυσαλίδα. Η σχεδίαση
                    ζει πλέον στο `CommentBubble` και **δεν** επαναλαμβάνεται εδώ:
                    όσο ήταν σήμανση μέσα σε αυτό το αρχείο, οι σημειώσεις των
                    καθηκόντων δεν είχαν τι να επαναχρησιμοποιήσουν. */}
                <ul className="flex flex-col gap-4">
                    {comments.map((comment) => (
                        <CommentBubble
                            key={comment.id}
                            author={comment.author}
                            text={comment.text}
                            createdAt={comment.createdAt}
                        />
                    ))}
                </ul>

                {canWrite ? (
                    <form onSubmit={submitComment} className="flex flex-col gap-2">
                        {commentError && (
                            <Notice status="danger" title={JUDGEMENTS.commentFailed}>
                                {commentError}
                            </Notice>
                        )}

                        {/* D44: το πεδίο του σχολίου δέχεται αναφορές. Ο κατάλογος
                            προσφέρει **μόνο** μέλη αυτής της συλλογής — ο φράχτης
                            είναι το 422 του API, αυτό εδώ είναι το να μη φτάσει
                            ποτέ ο χρήστης εκεί. */}
                        <MentionTextArea
                            value={text}
                            onChange={setText}
                            members={members}
                            isLoadingMembers={isLoadingMembers}
                            onMentionStart={onMentionStart}
                            label={JUDGEMENTS.comments}
                            isLabelHidden
                            rows={2}
                            placeholder={JUDGEMENTS.commentPlaceholder}
                        />
                        <p className="text-xs text-muted">{MENTIONS.hint}</p>
                        <Button
                            type="submit"
                            isPending={isSendingComment}
                            isDisabled={!text.trim()}
                            className="self-start"
                        >
                            {JUDGEMENTS.submitComment}
                        </Button>
                    </form>
                ) : (
                    <p className="text-xs text-muted">{JUDGEMENTS.readOnlyNote}</p>
                )}
            </div>

            <div className={`flex-col gap-3 ${pane === 'ratings' ? 'flex' : 'hidden'}`}>
                <h4 className="text-sm font-semibold">{JUDGEMENTS.ratings}</h4>

                {ratings.length === 0 && <p className="text-sm text-muted">{JUDGEMENTS.noRatings}</p>}

                <ul className="flex flex-col gap-3">
                    {ratings.map((rating) => (
                        <li key={rating.id} className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <RelevanceChip value={rating.relevance} />
                                <span className="text-sm font-medium">
                                    {rating.user?.fullName ?? rating.user?.username}
                                </span>
                                <TimeAgo date={rating.createdAt} className="text-xs text-muted" />
                            </div>
                            {rating.justification && (
                                <p className="text-sm text-muted">
                                    <MentionText text={rating.justification} />
                                </p>
                            )}
                        </li>
                    ))}
                </ul>

                {/* Β10δ: πόσοι **άλλοι** έχουν κρίνει. Το πλήθος βγαίνει από τη λίστα
                    `ratings`, που φορτώνεται με `collection` στο ερώτημα — είναι
                    επομένως **αυστηρά της τρέχουσας συλλογής** εξ ορισμού, και όχι
                    επειδή το φιλτράρει κάποιος εδώ. Άθροισμα όλων των συλλογών του
                    πόρου θα έλεγε στον χρήστη πόσοι τον έκριναν σε ομάδες που δεν
                    βλέπει, δηλαδή θα διέρρεε δραστηριότητα ιδιωτικής ομάδας. */}
                {canWrite && ratings.length > 0 && (
                    <p className="text-xs text-muted">
                        {othersCount > 0
                            ? JUDGEMENTS.othersRated(othersCount)
                            : JUDGEMENTS.onlyYouRated}
                    </p>
                )}

                {/* Β10γ: όταν υπάρχει ήδη εκτίμηση, η φόρμα **κλειδώνει** και δείχνει
                    την καταγεγραμμένη. Έμενε ανοιχτή, οπότε τίποτα στην οθόνη δεν
                    ξεχώριζε το «γράφω» από το «έγραψα» — και η μόνη ένδειξη ήταν ένα
                    μήνυμα που έσβηνε. */}
                {canWrite && myRating && !isEditingRating && (
                    /* Απαλό μωβ και όχι ουδέτερο: το πλαίσιο δηλώνει **τη δική σου**
                       κρίση ανάμεσα στις υπόλοιπες, και με το φόντο της επιφάνειας
                       χανόταν μέσα στη λίστα. Το ζεύγος κειμένου και φόντου μπήκε στον
                       έλεγχο αντίθεσης του `build-tokens.mjs`. */
                    <div className="flex flex-col gap-2 rounded-2xl border border-purple-400 bg-purple-100 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <i className="fa-solid fa-circle-check text-purple-800" aria-hidden="true" />
                            <span className="text-sm font-semibold text-purple-800">{JUDGEMENTS.recorded}</span>
                            <RelevanceChip value={myRating.relevance} />
                        </div>
                        <p className="text-sm">
                            {myRating.justification
                                ? <MentionText text={myRating.justification} />
                                : JUDGEMENTS.noJustification}
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="self-start"
                            onPress={() => { setEditingRating(true); setNotice(''); }}
                        >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                            {JUDGEMENTS.editRating}
                        </Button>
                    </div>
                )}

                {canWrite && (!myRating || isEditingRating) && (
                    <form onSubmit={submitRating} className="flex flex-col gap-3">
                        {notice && <Notice status="success" title={notice} onDismiss={() => setNotice('')} />}
                        {ratingError && (
                            <Notice status="danger" title={JUDGEMENTS.ratingFailed}>
                                {ratingError}
                            </Notice>
                        )}

                        <Select placeholder={SELECT_PLACEHOLDER} selectedKey={relevance} onSelectionChange={(key) => setRelevance(String(key))}>
                            <Label>{JUDGEMENTS.relevanceLabel}</Label>
                            <Select.Trigger>
                                <Select.Value />
                                <Select.Indicator />
                            </Select.Trigger>
                            <Select.Popover>
                                <ListBox items={RELEVANCE_OPTIONS}>
                                    {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                                </ListBox>
                            </Select.Popover>
                        </Select>

                        {/* Η αιτιολόγηση είναι **κείμενο συνομιλίας** όπως το
                            σχόλιο: η D16 την επέλεξε ακριβώς ως κείμενο, ώστε η
                            εκτίμηση να μην προσκαλεί μέσο όρο. */}
                        <MentionTextArea
                            value={justification}
                            onChange={setJustification}
                            members={members}
                            isLoadingMembers={isLoadingMembers}
                            onMentionStart={onMentionStart}
                            label={JUDGEMENTS.justificationLabel}
                            rows={2}
                            placeholder={JUDGEMENTS.justificationHint}
                        />

                        <div className="flex items-center gap-2">
                            <Button type="submit" isPending={isSavingRating}>
                                {myRating ? JUDGEMENTS.updateRating : JUDGEMENTS.submitRating}
                            </Button>
                            {myRating && (
                                <Button
                                    variant="outline"
                                    type="button"
                                    onPress={() => {
                                        // Επαναφορά στην καταγεγραμμένη τιμή: αλλιώς η
                                        // ακύρωση θα κρατούσε τις μη αποθηκευμένες
                                        // αλλαγές και η επόμενη επεξεργασία θα ξεκινούσε
                                        // από αυτές — σιωπηλή ακύρωση, κατηγορία Β.
                                        setRelevance(myRating.relevance);
                                        setJustification(myRating.justification ?? '');
                                        setEditingRating(false);
                                    }}
                                >
                                    {JUDGEMENTS.cancelRating}
                                </Button>
                            )}
                        </div>
                    </form>
                )}
            </div>

        </section>
    );
}
